import type { ComponentType, ReactNode, SVGProps } from 'react'
import { FormattedMessage } from 'react-intl'

import { IconGtoWizardLogoSigned } from '@gto/ui/assets'
import { Badge, type BadgeProps } from '@gto/ui/badge'
import { Button } from '@gto/ui/button'
import { cn } from '@gto/ui/cn'
import { Divider } from '@gto/ui/divider'
import { Paper } from '@gto/ui/paper'
import { Typography } from '@gto/ui/typography'

import { useAuth } from '@/hooks/use-auth'

// Subset of the DS Badge's own color union that this screen tints an icon circle
// for. Derived from BadgeProps (single source of truth) via Extract, so dropping
// or renaming a DS Badge color breaks the type-check here instead of drifting
// silently. The narrowing to these four is intentional — primary/secondary have
// no icon-circle token below.
type BadgeColor = Extract<
  NonNullable<BadgeProps['color']>,
  'warning' | 'error' | 'success' | 'info'
>

/**
 * Shared full-screen layout for an unauthenticated-style "gate" screen — a logged-in
 * partner held outside the portal. One owner of the Paper/Badge/logo chrome so any
 * gate stays visually consistent and only its copy/colour differ via props. Sole
 * consumer today is `beta-locked`; the T&C gate has its own onboarding wizard.
 */
export type GateScreenProps = {
  /** Heroicon for the status circle. */
  icon: ComponentType<SVGProps<SVGSVGElement>>
  /** Tints the icon circle and the badge. */
  color: BadgeColor
  badgeLabel: ReactNode
  title: ReactNode
  description: ReactNode
}

const ICON_CIRCLE_CLASS: Record<BadgeColor, string> = {
  warning: 'bg-warning-main/10 text-warning-main',
  error: 'bg-error-main/10 text-error-main',
  success: 'bg-success-main/10 text-success-main',
  info: 'bg-info-main/10 text-info-main',
}

export const GateScreen = ({
  icon: Icon,
  color,
  badgeLabel,
  title,
  description,
}: GateScreenProps) => {
  const { partner, onLogout } = useAuth()

  return (
    <div className="flex h-[100dvh] items-center justify-center overflow-hidden bg-gradient-primary-vertical p-6">
      <Paper
        className="w-full max-w-[440px] rounded-lg p-8 sm:p-6"
        variant="outlined"
      >
        <div className="flex flex-col items-center gap-6">
          <div className="flex items-center justify-between">
            <IconGtoWizardLogoSigned style={{ height: 20, width: 'auto' }} />
            <Badge color={color}>{badgeLabel}</Badge>
          </div>

          <Divider />

          <div
            className={cn(
              'flex size-16 items-center justify-center rounded-full',
              ICON_CIRCLE_CLASS[color],
            )}
          >
            <Icon
              height={32}
              width={32}
            />
          </div>

          <div className="flex flex-col items-center gap-2">
            <Typography
              className="font-bold"
              color="text.primary"
              variant="h5"
            >
              {title}
            </Typography>
            <Typography
              className="max-w-[340px]"
              color="text.secondary"
              variant="body2"
            >
              {description}
            </Typography>
          </div>

          {partner?.display_name != null && partner.display_name !== '' && (
            <Typography
              color="text.secondary"
              variant="caption"
            >
              <FormattedMessage
                id="gateScreen.signedInAs"
                values={{ name: partner.display_name }}
              />
            </Typography>
          )}

          <Button
            fullWidth
            className="mt-2"
            color="secondary"
            variant="outlined"
            onClick={() => onLogout()}
          >
            <FormattedMessage id="nav.logout" />
          </Button>
        </div>
      </Paper>
    </div>
  )
}
